import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Switch
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE_URL } from '../../../config/api';
import useIsMobile from '../useIsMobile';
import './AssistantSettingsTheme.css';
const ApiKeyManagement = () => {
  const isMobile = useIsMobile();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Generation Dialog State
  const [openGenerate, setOpenGenerate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKeyData, setGeneratedKeyData] = useState(null); // { api_key, name }
  const [generating, setGenerating] = useState(false);

  // Fetch Keys
  const fetchKeys = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/api-keys/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load API keys');
      
      const data = await response.json();
      setKeys(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  // Generate Key Handler
  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/api-keys/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newKeyName || 'Default Key'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate key');
      }

      setGeneratedKeyData(data); // Store the raw key to show user
      setOpenGenerate(false); // Close input dialog
      setNewKeyName('');
      fetchKeys(); // Refresh list

    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Toggle Status Handler
  const handleToggleStatus = async (key) => {
    const newStatus = !key.is_active;
    
    // Optimistic UI update
    const updatedKeys = keys.map(k => k.id === key.id ? { ...k, is_active: newStatus } : k);
    setKeys(updatedKeys);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/api-keys/${key.id}/status`, {
        method: 'PATCH',
        headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');
      
      // No need to re-fetch if successful, optimistic update holds
    } catch (err) {
      setError(err.message);
      fetchKeys(); // Revert on error
    }
  };

  // Revoke Key Handler
  const handleRevoke = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to revoke key');
      
      fetchKeys();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
      <Box className="assistantSettingsRoot">
      <Box className="assistantSettingsHeaderRow">
        <Typography variant="h6" className="assistantSettingsTitle">API Keys</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenGenerate(true)}
          className="assistantPrimaryButton"
        >
          Create New Key
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Success Banner for New Key */}
      {generatedKeyData && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setGeneratedKeyData(null)}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            New Key Generated: {generatedKeyData.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {isMobile
              ? 'Copy it now, it will not be shown again.'
              : 'Please copy this key now. You will not be able to see it again.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
          <TextField
            size="small"
            fullWidth
            value={generatedKeyData.api_key}
            InputProps={{ readOnly: true }}
            variant="outlined"
            className="assistantMonoInput"
          />
          <Button variant="outlined" className="assistantOutlineButton" onClick={() => copyToClipboard(generatedKeyData.api_key)}startIcon={<ContentCopyIcon />}>Copy</Button>
          </Box>
        </Alert>
      )}

      {/* Keys List */}
      <TableContainer
        component={Paper}
        elevation={0}
        className="assistantTablePanel assistantTableScroll"
      >
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {!isMobile && <TableCell>Prefix</TableCell>}
              {!isMobile && <TableCell>Created</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 5} align="center">No API keys found.</TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>
                    {key.name || 'Unnamed Key'}
                    {/* Prefix has no column of its own on mobile, show it under the name */}
                    {isMobile && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        className="assistantMonoChip"
                      >
                        {key.prefix}...
                      </Typography>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Chip
                        label={key.prefix + '...'}
                        size="small"
                        variant="outlined"
                        className="assistantMonoChip"
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                            checked={key.is_active}
                            onChange={() => handleToggleStatus(key)}
                            color="success"
                            size="small"
                        />
                        {!isMobile && (
                          <Typography variant="caption" sx={{ ml: 1 }}>
                              {key.is_active ? 'Active' : 'Paused'}
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke Key">
                      <IconButton
                        size={isMobile ? 'small' : 'medium'}
                        className="assistantIconButtonDanger"
                        onClick={() => handleRevoke(key.id)}
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate Key Dialog */}
      <Dialog
        open={openGenerate}
        onClose={() => setOpenGenerate(false)}
        fullScreen={isMobile}
        fullWidth
        PaperProps={{ className: "assistantDialogPaper" }}
      >
        <DialogTitle className="assistantDialogTitle">Generate New API Key</DialogTitle>
        <DialogContent>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
              Enter a friendly name for this key to identify it later.
            </Typography>
          )}
          <TextField
            sx={{ mt: isMobile ? 2 : 0 }}
            autoFocus
            margin="dense"
            label="Key Name (e.g., Website Widget)"
            fullWidth
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="assistantDialogActions">
          <Button onClick={() => setOpenGenerate(false)} variant="outlined" className="assistantOutlineButton">
            Cancel
          </Button>
          <Button onClick={handleGenerate} variant="contained" disabled={generating} className="assistantPrimaryButton">
            {generating ? "Generating..." : "Generate Key"}
          </Button>
        </DialogActions>

      </Dialog>
    </Box>
  );
};

export default ApiKeyManagement;